import type { ReactNode } from "react";
import clsx from "clsx";
import styles from "./PageHeader.module.scss";

export interface PageHeaderProps {
  title: ReactNode;
  /**
   * Heading level. `h1` (default) for the page's own title; `h2` when the header opens a *section*
   * within a page. Card titles are `h3` and belong to `<Card>`, not here.
   */
  as?: "h1" | "h2";
  /** Set when something needs `aria-labelledby` pointed at this heading. */
  id?: string;
  /**
   * An optional one-line description rendered BELOW the title. This is NOT the eyebrow the PO removed
   * (that sat ABOVE the title and repeated the nav) — it is a page's own subtitle, e.g. Fans' "Your
   * marketing audience — list, broadcast, and track growth."
   */
  subtitle?: ReactNode;
  /** Right-aligned actions (a toggle, a button). */
  actions?: ReactNode;
  className?: string;
}

/**
 * The page/section title — what the shipped sheet renders as `.section-toggle__text`: a display-face
 * title with its actions as a sibling.
 *
 * **There is no eyebrow, at either level** (product-owner ruling, 2026-08-17). The sheet and the
 * provided guide (§11 "Eyebrow + Title pair", §14 "Eyebrow + title card") both pair one with the
 * title; the PO overruled both, because the category label was repeating what the nav already said.
 * The prop is gone rather than deprecated, so there is nothing to reach for —
 * `headings.guard.test.ts` covers the pages that still hand-roll a header.
 *
 * It exists because this pair was copy-pasted into nine stylesheets, which is why every typographic
 * ruling (caps, mono, Archivo, weight) turned into a multi-file sweep. Change it here now.
 */
export function PageHeader({ title, as = "h1", id, subtitle, actions, className }: PageHeaderProps) {
  const Heading = as;
  return (
    <header className={clsx(styles.head, className)}>
      <div className={styles.text}>
        <Heading id={id} className={styles.title}>
          {title}
        </Heading>
        {subtitle != null && <p className={styles.subtitle}>{subtitle}</p>}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
